/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewEncapsulation } from '@angular/core';
import { assign, get, isNil, trim } from 'lodash';
import { Router } from '@angular/router';
import { BuildingAlarmCardService } from '@app/energy-guard/services/building-alarm-card.service';
import { catchError, filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '@app/shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { BuildingHistoryDialogComponent } from '@app/energy-map/dialog/building-history-dialog/building-history-dialog.component';
import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { EfficiencyAnalysisDatastoreService } from '@app/efficiency-analysis/services/efficiency-analysis-datastore.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { BuildingEfficiencyWizardStore } from '@app/efficiency-analysis/components/building-efficiency-wizard/building-efficiency-wizard.component.store';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { SensorDataService } from '@app/sensordaten/services/sensor-data.service';
import { SensorListObject } from '@app/energy-map/interfaces/sensor-list';
import { Observable, Subject, of } from 'rxjs';
import { TransformedNode } from '@app/sensordaten/models/sensors-by-municipality-nodes';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { SensorFilter } from '@app/sensordaten/models/sensor-filter';
import { BuildingTypes } from '@app/shared/models/building-types';
@Component({
  selector: 'app-description-consumer',
  templateUrl: './description-consumer.component.html',
  styleUrls: ['./description-consumer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DescriptionConsumerComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() data: any;
  @Output() saveEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() efficiencyAnalysisDisplayEmitter = new EventEmitter();
  @Output() closeEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() isFromDescriptionConsumer = new EventEmitter();
  public building: any;
  public building$ = this.buildingStoreService.selectedBuilding$;
  public buildingTypes: any[] = [];
  public errorMessage = '';
  public hasBuildingId = false;
  public isDetailMode = true;
  public isEditLocation = false;
  public isEditMode = false;
  public sensors: SensorListObject[] = [];
  public sensorRouterParams: Record<string, string | boolean> = {
    returnstate: 'openbuildingdialog',
    reroute: false,
  };
  private previousData: any;

  constructor(
    public authService: AuthService,
    public efficiencyAnalysisService: EfficiencyAnalysisService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private _efficiencyAnalysisApiService: EfficiencyAnalysisApiService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private buildingAlarmCardService: BuildingAlarmCardService,
    private buildingStoreService: BuildingsStoreService,
    private buildingTypeService: BuildingTypesService,
    private efficiencyAnalysisDataStore: EfficiencyAnalysisDatastoreService,
    private matDialog: MatDialog,
    private overlayLoaderService: OverlayLoaderService,
    private router: Router,
    private sensorDataService: SensorDataService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    if (this.data) {
      this.buildingStoreService.setSelectedBuildingStore(this.data);
      this.setBuildingTypes();
      this.setBuildingAndSensors();

      this.buildingStoreService.selectingBuildingWithUnkownTypeOrGps$
        .pipe(
          filter((building) => !!building),
          filter(() => !!this.buildingStoreService.selectedBuilding),
          filter(() => this.isDetailMode),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((building) => this.closeEmitter.emit(building));

      this.buildingStoreService.selectingBuildingWithUnkownTypeOrGps$
        .pipe(
          filter((building) => !!building),
          filter(() => !this.isDetailMode),
          takeUntil(this.unsubscribe$)
        )
        .subscribe((building) => {
          this.matDialog
            .open(NotificationDialogComponent, {
              data: {
                title: 'Gebäudedetails bearbeiten',
                descriptionText: 'Möchtest du deine Änderungen speichern?',
                declineText: 'Abbrechen',
                declineFaClass: 'fa-times-circle',
                confirmText: 'Speichern',
                confirmFaClass: 'fa-check-circle',
                alignment: 'start',
                orientation: 'row',
                hideClose: true,
              },
              width: '700px',
              disableClose: true,
            })
            .afterClosed()
            .subscribe((res) => {
              if (res === true) {
                this.save();
                this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.buildingTypeSave, {
                  buildingType: this.building.buildingType.name,
                  municipalityName: this.data.municipalityName,
                  roleEdited: this.authService.currentUser.roleName,
                });
                this.closeEmitter.emit(building);
              } else {
                this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps = null;
              }
            });
        });

      this.hasBuildingId = !isNil(this.data.buildingTypeId);
      const buildingId = isNil(this.building.buildingType.guid) ? this.building.buildingType.buildingTypeId : this.building.buildingType.guid;
      this.efficiencyAnalysisService.setEfficiencyAnalysisStateByGuid(buildingId);
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getSensorList(buildingData: any): Observable<SensorFilter<TransformedNode[]>> {
    this.sensors = [];
    return this.sensorDataService.getSensorsByMunicipality(buildingData.municipalityId);
  }

  public save(): void {
    if (isNil(this.building.buildingType.guid) && this.building.buildingType.buildingTypeId !== this.building.buildingTypeId) {
      this.building.averageElectricityConsumption = null;
      this.building.averageHeatConsumption = null;
      this.building.buildingTypeId = this.building.buildingType.buildingTypeId;
      this.building.districtHeatingConsumption = null;
      this.building.electricityColorStatus = null;
      this.building.electricityComputedValue = null;
      this.building.electricityConsumption = null;
      this.building.electricityGuideValue = null;
      this.building.electricityHeatConsumption = null;
      this.building.electricityMaxDataRange = null;
      this.building.electricityMinDataRange = null;
      this.building.efficiencyPercentageElectricity = null;
      this.building.floorArea = null;
      this.building.heatColorStatus = null;
      this.building.heatComputedValue = null;
      this.building.heatGuideValue = null;
      this.building.heatMaxDataRange = null;
      this.building.heatMinDataRange = null;
      this.building.heatingOilConsumption = null;
      this.building.heatingTypeId = null;
      this.building.lpgConsumption = null;
      this.building.naturalGasConsumption = null;
      this.building.pelletWoodConsumption = null;
      this.building.efficiencyPercentageHeat = null;
      this.building.efficiencyAnalysisDate = null;
      this.building.efficiencyAnalysisStartDate = null;
      this.building.efficiencyAnalysisEndDate = null;
      this.building.notes = null;
      this.building.efficiencyAnalysisMetersID = null;
    }

    const buildingId = isNil(this.building.buildingType.guid) ? this.building.buildingType.buildingTypeId : this.building.buildingType.guid;
    this.efficiencyAnalysisService.setEfficiencyAnalysisStateByGuid(buildingId);

    this.errorMessage = '';
    this.data = { ...this.building };
    this.buildingStoreService.setSelectedBuildingStore(this.data);
    this.saveEmitter.emit(this.building);
    this.isDetailMode = true;
    this.isEditMode = false;
    this.isEditLocation = false;
    this.isFromDescriptionConsumer.emit();

    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    this._efficiencyAnalysisApiService
      .getByBuildingId(this.data.guid, municipalityKey)
      .pipe(
        switchMap((analysis: EfficiencyAnalysisDto[]) => {
          if (analysis.length > 0) {
            const oldData = this.mapSaveData(analysis[0]);
            const newData = this.mapSaveData(this.data);
            const isEqual = Object.keys(oldData).every((key) => oldData[key] === newData[key]);

            if (isEqual) {
              return this.efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis();
            } else {
              return this.efficiencyAnalysisDataStore
                .deleteEfficiencyAnalysis(this.data.guid, municipalityKey)
                .pipe(switchMap(() => this.efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis()));
            }
          } else {
            return of(null);
          }
        }),
        catchError(() => {
          return this._buildingEfficiencyWizardStore
            .saveEfficiencyAnalysis(this.mapSaveData(this.data), municipalityKey)
            .pipe(switchMap(() => this.efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis()));
        }),
        finalize(() => (this.overlayLoaderService.isLoading = false)),
        first()
      )
      .subscribe();
  }

  private mapSaveData(data: EfficiencyAnalysisDto, guid: string | null = null): Partial<EfficiencyAnalysisDto> {
    const saveData: Partial<EfficiencyAnalysisDto> = {
      buildingId: data.buildingId ? data.buildingId : data.guid,
      floorArea: data.floorArea,
      electricityConsumption: data.electricityConsumption,
      averageElectricityConsumption: data.averageElectricityConsumption,
      electricityGuideValue: data.electricityGuideValue,
      electricityMinDataRange: data.electricityMinDataRange,
      electricityMaxDataRange: data.electricityMaxDataRange,
      heatingOilConsumption: data.heatingOilConsumption,
      naturalGasConsumption: data.naturalGasConsumption,
      lpgConsumption: data.lpgConsumption,
      districtHeatingConsumption: data.districtHeatingConsumption,
      electricityHeatConsumption: data.electricityHeatConsumption,
      pelletWoodConsumption: data.pelletWoodConsumption,
      averageHeatConsumption: data.averageHeatConsumption,
      heatGuideValue: data.heatGuideValue,
      heatMinDataRange: data.heatMinDataRange,
      heatMaxDataRange: data.heatMaxDataRange,
      electricityColorStatus: data.electricityColorStatus,
      heatColorStatus: data.heatColorStatus,
      electricityComputedValue: data.electricityComputedValue,
      heatComputedValue: data.heatComputedValue,
      efficiencyAnalysisDate: data.efficiencyAnalysisDate,
      efficiencyPercentageElectricity: data.efficiencyPercentageElectricity,
      efficiencyPercentageHeat: data.efficiencyPercentageHeat,
      efficiencyAnalysisStartDate: data.efficiencyAnalysisStartDate,
      efficiencyAnalysisEndDate: data.efficiencyAnalysisEndDate,
      isElectricityValueSuggested: data.isElectricityValueSuggested,
      isHeatValueSuggested: data.isHeatValueSuggested,
      notes: data.notes,
    };
    if (!isNil(guid)) {
      saveData.guid = guid;
    }
    return saveData;
  }

  back() {
    this.data = this.mapPreviousDataToData(this.previousData, this.data);
    this.building = { ...this.data };
    this.buildingStoreService.setSelectedBuildingStore(this.data);
    this.isDetailMode = true;
    this.isEditMode = false;
    this.isEditLocation = false;
  }

  setType(buildingType: any) {
    this.building = assign(this.building, {
      buildingType: {
        name: buildingType.name,
        buildingTypeId: buildingType.guid,
        imageName: buildingType.imageName ? buildingType.imageName : null,
        targetEfficency: buildingType.targetProficiency,
      },
    });

    this.hasBuildingId = !isNil(this.building);
  }

  displayEfficiencyAnalysisWizard() {
    this.efficiencyAnalysisDisplayEmitter.emit();
  }

  gotoMeterListPage(connectionObjectNumber: string, hasLicensedMeters: boolean) {
    if (hasLicensedMeters) {
      this.buildingAlarmCardService.collapseAll();
      this.router.navigate(['/energy-guard/building-alarm/'], {
        queryParams: {
          connectionobjectnumber: connectionObjectNumber,
          returnroute: 'energiekarte',
          returnstate: 'openbuildingdialog',
          buildingid: this.data.guid,
        },
      });
    } else {
      this.router.navigate(['/energy-guard/teaser-main/'], {
        queryParams: {
          returnroute: 'energiekarte',
          returnstate: 'openbuildingdialog',
          buildingid: this.data.guid,
        },
      });
    }
  }

  goToRoute(connectionObjectNumberValue: string, route: string) {
    this.router.navigate([`/${route}/`], {
      queryParams: {
        connectionobjectnumber: connectionObjectNumberValue,
        action: 'filterbybuilding',
        returnroute: 'energiekarte',
        returnstate: 'openbuildingdialog',
        buildingid: this.data.guid,
        guid: this.data.meters[0]?.guid,
        reroute: true,
      },
    });
  }

  trackByFn(_: number, item: any) {
    return item.guid;
  }

  openHistoryDialog() {
    this.matDialog.open(BuildingHistoryDialogComponent, {
      data: {
        buildingId: this.data.guid,
      },
      position: { top: '140px', left: '500px' },
      panelClass: ['-sm'],
    });
  }

  editLocation = () => {
    this.isEditLocation = true;
    this.isEditMode = false;
    this.isDetailMode = false;
  };

  editBuildingDetails = () => {
    this.previousData = { ...this.data };
    this.isEditMode = true;
    this.isDetailMode = false;
    this.isEditLocation = false;
  };

  closeEditLocation = () => {
    this.isEditMode = true;
    this.isEditLocation = false;
    this.isDetailMode = false;
  };

  mapPreviousDataToData = (previousData: any, data: any) => {
    data.buildingType = previousData.buildingType;
    data.name = previousData.name;
    data.description = previousData.description;

    return data;
  };

  private setBuildingTypes(): void {
    this.buildingTypeService
      .getAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((buildingTypes: BuildingTypes[]) => {
        this.buildingTypes = buildingTypes;
        this.buildingTypes.sort((a, b) => a.name.localeCompare(b.name));
      });
  }

  private setBuildingAndSensors(): void {
    this.building$
      .pipe(
        tap((building) => {
          this.building = building;
          this.sensorRouterParams = {
            buildingid: building.guid,
          };
        }),
        switchMap((building) => this.getSensorList(building)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((res: SensorFilter<TransformedNode[]>) => {
        const sensors: TransformedNode[] = get(res, 'data.sensorFilter.nodes', []);
        const buildingSensors = sensors.filter(
          (node: TransformedNode) => !isNil(node.device_eui) && trim(node.device_eui).length > 0 && node.buildingID === this.building.guid
        );
        this.sensors = buildingSensors.map((node: TransformedNode) => {
          return {
            alarmRuleEnabled: node.alarmRuleEnabled,
            device_eui: node.device_eui,
            device_id: node.device_id,
            device_name: node.device_name,
            locationContext: node.locationContext,
            sensor_id: node.sensor_id,
            sensor_type: node.sensor_type,
            reading: node.reading,
            currentQuality: node.currentQuality,
            ruleJSON: node.ruleJSON,
          };
        });
      });
  }
}
